// apicontrol.ui/src/components/Services/Services.tsx
// COMPLETE VERSION: Includes safe deletion with dependency checking
import React, { useState, useEffect } from 'react';
import { apiServicesService } from '../../services/apiServicesService';
import { ApiService, CreateApiServiceRequest } from '../../types';
import { Settings, Globe, Plus, Edit, Power, Copy, CheckCircle, Trash2, AlertTriangle } from 'lucide-react';
import './Services.css';

interface ServiceDependencies {
  canDelete: boolean;
  permissionsCount: number;
  activePermissionsCount: number;
  usageRecordsCount: number;
  message: string;
}

const Services: React.FC = () => {
  const [services, setServices] = useState<ApiService[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewServiceModal, setShowNewServiceModal] = useState(false);
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [selectedService, setSelectedService] = useState<ApiService | null>(null);
  const [deleteDependencies, setDeleteDependencies] = useState<ServiceDependencies | null>(null);
  const [copiedGuid, setCopiedGuid] = useState<string | null>(null);
  
  const [serviceForm, setServiceForm] = useState<CreateApiServiceRequest>({
    serviceName: '',
    displayName: '',
    description: '',
    serviceGuid: '',
    baseUrl: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await apiServicesService.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const copyToClipboard = async (guid: string) => {
    try {
      await navigator.clipboard.writeText(guid);
      setCopiedGuid(guid);
      setTimeout(() => setCopiedGuid(null), 2000);
    } catch (error) {
      console.error('Failed to copy GUID:', error);
    }
  };

  const createService = async () => {
    try {
      const formData = {
        ...serviceForm,
        serviceGuid: serviceForm.serviceGuid || generateGuid()
      };
      
      await apiServicesService.create(formData);
      setShowNewServiceModal(false);
      setServiceForm({
        serviceName: '',
        displayName: '',
        description: '',
        serviceGuid: '',
        baseUrl: ''
      });
      fetchServices();
    } catch (error: any) {
      alert('Failed to create service: ' + (error.response?.data?.message || error.message));
    }
  };

  const updateService = async () => {
    if (!selectedService) return;
    
    try {
      await apiServicesService.update(selectedService.id, serviceForm);
      setShowEditServiceModal(false);
      setSelectedService(null);
      fetchServices();
    } catch (error: any) {
      alert('Failed to update service: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleServiceStatus = async (service: ApiService) => {
    try {
      await apiServicesService.update(service.id, { isActive: !service.isActive });
      fetchServices();
    } catch (error: any) {
      alert('Failed to update service status: ' + (error.response?.data?.message || error.message));
    }
  };

  // NEW: Check dependencies before attempting deletion
  const checkDependenciesAndDelete = async (service: ApiService) => {
    try {
      const response = await apiServicesService.checkDependencies(service.id);
      setDeleteDependencies(response.data);
      setSelectedService(service);
      setShowDeleteWarningModal(true);
    } catch (error: any) {
      console.error('Error fetching dependencies:', error);
      alert('Failed to check service dependencies. Please try again.');
    }
  };

  // NEW: Perform actual deletion after confirmation
  const confirmDelete = async () => {
    if (!selectedService) return;

    try {
      await apiServicesService.delete(selectedService.id);
      setShowDeleteWarningModal(false);
      setSelectedService(null);
      setDeleteDependencies(null);
      fetchServices();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message;
      const suggestion = error.response?.data?.suggestion;
      alert(`Failed to delete service: ${errorMsg}${suggestion ? '\n\n' + suggestion : ''}`);
    }
  };

  const openEditModal = (service: ApiService) => {
    setSelectedService(service);
    setServiceForm({
      serviceName: service.serviceName,
      displayName: service.displayName,
      description: service.description || '',
      serviceGuid: service.serviceGuid,
      baseUrl: service.baseUrl || ''
    });
    setShowEditServiceModal(true);
  };

  if (loading) return <div className="loading">Loading services...</div>;

  return (
    <div className="services">
      <div className="page-header">
        <h2 className="page-title">API Services</h2>
        <button className="btn btn-primary" onClick={() => setShowNewServiceModal(true)}>
          <Plus size={16} />
          Add Service
        </button>
      </div>

      <div className="services-grid">
        {services.map(service => (
          <div key={service.id} className="service-card">
            <div className="service-card-header">
              <div className="service-icon">
                <Settings size={24} />
              </div>
              <span className={`status-badge ${service.isActive ? 'status-active' : 'status-inactive'}`}>
                {service.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <h3 className="service-name">{service.displayName}</h3>
            
            <div className="service-info">
              <div className="service-info-item">
                <Globe size={16} />
                <span className="service-url">{service.baseUrl || 'No URL configured'}</span>
              </div>
            </div>

            <div className="service-guid-section">
              <label className="guid-label">Service GUID</label>
              <div className="guid-container">
                <code className="guid-value">{service.serviceGuid}</code>
                <button 
                  className="btn-copy"
                  onClick={() => copyToClipboard(service.serviceGuid)}
                  title="Copy GUID"
                >
                  {copiedGuid === service.serviceGuid ? (
                    <CheckCircle size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
              </div>
              <span className="guid-hint">Use this GUID in your service configuration</span>
            </div>
            
            {service.description && (
              <p className="service-description">{service.description}</p>
            )}
            
            <div className="service-meta">
              <span>Service Name: {service.serviceName}</span>
              <span>Created: {new Date(service.createdDate).toLocaleDateString()}</span>
            </div>
            
            <div className="service-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => openEditModal(service)}>
                <Edit size={16} />
                Edit
              </button>
              <button 
                className={`btn btn-sm ${service.isActive ? 'btn-danger' : 'btn-success'}`}
                onClick={() => toggleServiceStatus(service)}
              >
                <Power size={16} />
                {service.isActive ? 'Disable' : 'Enable'}
              </button>
              <button 
                className="btn btn-danger btn-sm"
                onClick={() => checkDependenciesAndDelete(service)}
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {services.length === 0 && (
        <div className="empty-state">
          <Settings className="empty-state-icon" size={48} />
          <p className="empty-state-title">No API Services</p>
          <p className="empty-state-description">Add your first API service to get started.</p>
        </div>
      )}

      {/* New Service Modal */}
      {showNewServiceModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Add New API Service</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Service Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., phonenumber"
                  value={serviceForm.serviceName}
                  onChange={(e) => setServiceForm({...serviceForm, serviceName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Display Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Phone Number Validation"
                  value={serviceForm.displayName}
                  onChange={(e) => setServiceForm({...serviceForm, displayName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  Service GUID
                  <span className="optional-label"> (optional - auto-generated if blank)</span>
                </label>
                <div className="guid-input-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
                    value={serviceForm.serviceGuid}
                    onChange={(e) => setServiceForm({...serviceForm, serviceGuid: e.target.value})}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setServiceForm({...serviceForm, serviceGuid: generateGuid()})}
                  >
                    Generate
                  </button>
                </div>
                <span className="form-hint">Unique identifier for this service</span>
              </div>
              <div className="form-group">
                <label className="form-label">Base URL</label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="e.g., https://api1.example.com"
                  value={serviceForm.baseUrl}
                  onChange={(e) => setServiceForm({...serviceForm, baseUrl: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="Describe what this API service does..."
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNewServiceModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={createService}>
                Create Service
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {showEditServiceModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Edit API Service</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Service Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={serviceForm.serviceName}
                  onChange={(e) => setServiceForm({...serviceForm, serviceName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Display Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={serviceForm.displayName}
                  onChange={(e) => setServiceForm({...serviceForm, displayName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Service GUID</label>
                <div className="guid-display-readonly">
                  <code>{serviceForm.serviceGuid}</code>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => copyToClipboard(serviceForm.serviceGuid || '')}
                    title="Copy GUID"
                  >
                    {copiedGuid === serviceForm.serviceGuid ? (
                      <CheckCircle size={16} />
                    ) : (
                      <Copy size={16} />
                    )}
                  </button>
                </div>
                <span className="form-hint">GUID cannot be changed after creation</span>
              </div>
              <div className="form-group">
                <label className="form-label">Base URL</label>
                <input
                  type="url"
                  className="form-input"
                  value={serviceForm.baseUrl}
                  onChange={(e) => setServiceForm({...serviceForm, baseUrl: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditServiceModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={updateService}>
                Update Service
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Warning Modal - NEW */}
      {showDeleteWarningModal && selectedService && deleteDependencies && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">
                {deleteDependencies.canDelete ? 'Confirm Deletion' : 'Cannot Delete Service'}
              </h3>
            </div>
            <div className="modal-body">
              {!deleteDependencies.canDelete && (
                <div className="error-message">
                  <AlertTriangle size={20} />
                  <div>
                    <strong>This service has dependencies and cannot be deleted:</strong>
                    <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                      {deleteDependencies.permissionsCount > 0 && (
                        <li>{deleteDependencies.permissionsCount} permission(s) ({deleteDependencies.activePermissionsCount} active)</li>
                      )}
                      {deleteDependencies.usageRecordsCount > 0 && (
                        <li>{deleteDependencies.usageRecordsCount} usage record(s)</li>
                      )}
                    </ul>
                    <p style={{ marginTop: '1rem' }}>
                      <strong>Suggestion:</strong> Disable the service instead of deleting it.
                    </p>
                  </div>
                </div>
              )}
              
              {deleteDependencies.canDelete && (
                <div>
                  <p>Are you sure you want to delete <strong>{selectedService.displayName}</strong>?</p>
                  <p style={{ marginTop: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                    Service GUID: <code>{selectedService.serviceGuid}</code>
                  </p>
                  <p style={{ marginTop: '0.5rem', color: '#dc2626', fontSize: '0.875rem' }}>
                    This action cannot be undone.
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowDeleteWarningModal(false);
                  setSelectedService(null);
                  setDeleteDependencies(null);
                }}
              >
                {deleteDependencies.canDelete ? 'Cancel' : 'Close'}
              </button>
              {deleteDependencies.canDelete && (
                <button className="btn btn-danger" onClick={confirmDelete}>
                  Delete Service
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
